import { createSlice } from "@reduxjs/toolkit";

// Safe JSON parse function
const safeParseJSON = (key, fallbackValue) => {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : fallbackValue;
  } catch (error) {
    console.warn(`Error parsing ${key}:`, error);
    return fallbackValue;
  }
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: safeParseJSON('cartItems', []),
    loading: false,
    shippingInfo: safeParseJSON('shippingInfo', {}),
    billingInfo: safeParseJSON('billingInfo', {}),
    cartItems: [],
    updateItems: []
  },
  reducers: {
    clearCart(state) {
      state.items = [];
      localStorage.removeItem('cartItems');
    },
    getCartItemsRequest(state) {
      state.loading = true;
    },
    getCartItemsSuccess(state, action) {
      state.items = action.payload;
      state.loading = false;
      localStorage.setItem('cartItems', JSON.stringify(state.items));
    },
    getCartItemsFail(state) {
      state.loading = false;
    },
    addCartItem(state, action) {
      const newItem = action.payload;
      const existingItemIndex = state.items.findIndex(item =>
        item.product === newItem.product &&
        (!newItem.variant || (item.variant && item.variant._id === newItem.variant._id))
      );

      if (existingItemIndex !== -1) {
        const updatedQuantity = state.items[existingItemIndex].quantity + newItem.quantity;
        state.items[existingItemIndex].quantity = Math.min(updatedQuantity, newItem.stock);
      } else {
        state.items.push(newItem);
      }

      localStorage.setItem('cartItems', JSON.stringify(state.items));
    },
    removeCartItem(state, action) {
      const { productId, variantId } = action.payload;
      state.items = state.items.filter(item => {
        const isSameProduct = item.product === productId;
        const isSameVariant =
          (!variantId && !item.variant) ||
          (variantId && item.variant && item.variant._id === variantId);

        return !(isSameProduct && isSameVariant);
      });

      localStorage.setItem('cartItems', JSON.stringify(state.items));
    },
    updateCartItemQuantity(state, action) {
      const { productId, quantity, stock, variantId } = action.payload;

      const cartItemIndex = state.items.findIndex(item => {
        return variantId
          ? item.variant && item.variant._id === variantId
          : item.product === productId;
      });

      if (cartItemIndex !== -1) {
        const updatedCartItem = { ...state.items[cartItemIndex], quantity };
        if (typeof stock !== 'undefined') {
          updatedCartItem.stock = stock;
        }
        state.items[cartItemIndex] = updatedCartItem;
      }

      localStorage.setItem('cartItems', JSON.stringify(state.items));
    },
    saveShippingInfo(state, action) {
      state.shippingInfo = action.payload;
      localStorage.setItem('shippingInfo', JSON.stringify(action.payload));
    },
    saveBillingInfo(state, action) {
      state.billingInfo = action.payload;
      localStorage.setItem('billingInfo', JSON.stringify(action.payload));
    },
    orderCompleted(state) {
      state.items = [];
      state.shippingInfo = {};
      state.billingInfo = {};
      localStorage.removeItem('shippingInfo');
      localStorage.removeItem('billingInfo');
      localStorage.removeItem('cartItems');
      sessionStorage.removeItem('orderInfo');
    },
    vaildateCartItemsRequest(state) {
      state.loading = true;
    },
    vaildateCartItemsSuccess(state, action) {
      state.loading = false;
      state.cartItems = action.payload.cartItems;
    },
    vaildateCartItemsFail(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    updateItemsRequest(state) {
      state.loading = true;
    },
    updateItemsSuccess(state, action) {
      state.loading = false;
      state.items = action.payload.items;
    },
    updateItemsFail(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
  }
});

export const {
  addCartItem,
  removeCartItem,
  updateCartItemQuantity,
  clearCart,
  getCartItemsRequest,
  getCartItemsSuccess,
  getCartItemsFail,
  saveShippingInfo,
  saveBillingInfo,
  orderCompleted,
  vaildateCartItemsRequest,
  vaildateCartItemsSuccess,
  vaildateCartItemsFail,
  updateItemsRequest,
  updateItemsSuccess,
  updateItemsFail
} = cartSlice.actions;

export default cartSlice.reducer;
